// LearnCore Frontend - 金币余额显示组件

import React, { useState, useEffect } from 'react';
import { dkpApi } from '../api/tasks';
import type { DKPBalance as DKPBalanceType, DKPTransaction } from '../types/tasks-rewards';

interface DKPBalanceProps {
  userId?: string;
  showTransactions?: boolean;
  compact?: boolean;
  className?: string;
}

export const DKPBalanceComponent: React.FC<DKPBalanceProps> = ({
  userId,
  showTransactions = false,
  compact = false,
  className = ''
}) => {
  const [balance, setBalance] = useState<DKPBalanceType | null>(null);
  const [transactions, setTransactions] = useState<DKPTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log('[DKPBalance] Fetching DKP balance for userId:', userId);
        const balanceResponse = await dkpApi.getDKPBalance(userId);
        console.log('[DKPBalance] API response:', balanceResponse);

        if (balanceResponse) {
          // API返回格式: {balance: {...}, recent_transactions: [...]}
          const { balance, recent_transactions } = balanceResponse;

          if (balance) {
            setBalance(balance);
            console.log('[DKPBalance] Balance set:', balance);

            if (showTransactions && recent_transactions) {
              // 确保transactions是数组
              console.log('[DKPBalance] Recent transactions data:', recent_transactions, 'Type:', typeof recent_transactions);
              const transactionsArray = Array.isArray(recent_transactions) ? recent_transactions : [];
              console.log('[DKPBalance] Setting transactions to:', transactionsArray);
              setTransactions(transactionsArray);
            }
          } else {
            console.error('[DKPBalance] No balance data in response:', balanceResponse);
            setError('响应中缺少余额数据');
          }
        } else {
          console.error('[DKPBalance] Invalid response format:', balanceResponse);
          setError('API响应格式错误');
        }
      } catch (err: any) {
        console.error('[DKPBalance] Failed to fetch DKP balance:', err);
        const errorMessage = err.response?.data?.error || err.message || '获取DKP信息失败';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, showTransactions]);

  const getTransactionTypeText = (type: string): string => {
    switch (type) {
      case 'task_completion': return '任务完成';
      case 'reward_redemption': return '奖励兑换';
      case 'manual_adjustment': return '手动调整';
      case 'bonus': return '奖励';
      case 'penalty': return '惩罚';
      default: return type;
    }
  };

  const getTransactionIcon = (type: string): string => {
    switch (type) {
      case 'task_completion':
      case 'bonus':
        return '↑';
      case 'reward_redemption':
      case 'penalty':
        return '↓';
      case 'manual_adjustment':
        return '↔';
      default:
        return '•';
    }
  };

  const getTransactionColor = (amount: number): string => {
    if (amount > 0) return 'text-green-600';
    if (amount < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const formatAmount = (amount: number): string => {
    const sign = amount > 0 ? '+' : '';
    return `${sign}${amount}`;
  };

  if (loading) {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-8 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (error || !balance) {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-red-200 dark:border-red-800 p-4 ${className}`}>
        <div className="text-red-600 dark:text-red-400 text-sm">
          {error || '无法加载金币信息'}
        </div>
        {error && (
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            请检查网络连接或联系管理员
          </div>
        )}
      </div>
    );
  }

  if (compact) {
    return (
      <div className={`bg-gradient-to-r from-orange-500 to-yellow-500 rounded-lg p-3 text-white ${className}`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs opacity-90">当前金币</div>
            <div className="text-2xl font-bold">{balance.balance}</div>
          </div>
          <div className="text-right">
            <div className="text-xs opacity-90">累计获得</div>
            <div className="text-sm font-medium">+{balance.total_earned}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 ${className}`}>
      {/* 金币余额显示 */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900">金币积分</h3>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-orange-600">{balance.balance}</span>
            <span className="text-sm text-gray-500">金币</span>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="bg-green-50 rounded-lg p-3">
            <div className="text-green-600 font-medium">+{balance.total_earned}</div>
            <div className="text-green-700 text-xs">累计获得</div>
          </div>
          <div className="bg-red-50 rounded-lg p-3">
            <div className="text-red-600 font-medium">{balance.total_spent}</div>
            <div className="text-red-700 text-xs">累计消费</div>
          </div>
        </div>
      </div>

      {/* 交易记录 */}
      {showTransactions && (
        <div className="p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">最近交易</h4>
          {!Array.isArray(transactions) || transactions.length === 0 ? (
            <div className="text-center text-gray-500 text-sm py-4">
              暂无交易记录
            </div>
          ) : (
            <div className="space-y-2">
              {transactions.map((transaction) => (
                <div
                  key={transaction.id}
                  className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <span className={`text-lg font-medium ${getTransactionColor(transaction.amount)}`}>
                      {getTransactionIcon(transaction.transaction_type)}
                    </span>
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {getTransactionTypeText(transaction.transaction_type)}
                      </div>
                      {transaction.description && (
                        <div className="text-xs text-gray-500">{transaction.description}</div>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-medium ${getTransactionColor(transaction.amount)}`}>
                      {formatAmount(transaction.amount)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(transaction.created_at).toLocaleDateString('zh-CN')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DKPBalanceComponent;